import { USER_PUSH_PREFERENCE_KEYS } from './user-push-preferences.js';

export class UserRequestContractError extends Error {
  constructor(detail) {
    super(`Invalid user request: ${detail}`);
    this.name = 'UserRequestContractError';
    this.code = 'INVALID_API_REQUEST';
  }
}
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const integerQuery = value => typeof value === 'number' && Number.isSafeInteger(value)
  || typeof value === 'string' && /^-?\d+$/.test(value) && Number.isSafeInteger(Number(value));
export function readUserIdentifier(value) {
  if (typeof value !== 'string' || !/^[a-f0-9]{24}$/i.test(value)) throw new UserRequestContractError('expected an ObjectId.');
  return value;
}
export function readUserPushPreferencesRequest(payload) {
  if (!record(payload) || !USER_PUSH_PREFERENCE_KEYS.some(key => Object.hasOwn(payload, key))
    || USER_PUSH_PREFERENCE_KEYS.some(key => Object.hasOwn(payload, key) && typeof payload[key] !== 'boolean')) {
    throw new UserRequestContractError('expected at least one supported boolean push preference.');
  }
  return payload;
}
export function readUserDirectoryQuery(query = {}) {
  if (!record(query)
    || query.memberId !== undefined && (typeof query.memberId !== 'string' || !/^[a-f0-9]{24}$/i.test(query.memberId))
    || query.q !== undefined && (typeof query.q !== 'string' || query.q.length > 200)
    || query.roleFilter !== undefined && (typeof query.roleFilter !== 'string' || query.roleFilter.length > 100)
    || query.view === 'guardian' && (!['all', 'user', 'member'].includes(query.typeFilter || 'all') || !['all', 'Verified', 'Child Protection Certified', 'Local Assembly Member'].includes(query.certFilter || 'all') || query.all === 'true' || query.all === true)
    || ['view', 'certFilter', 'typeFilter'].some(key => query[key] !== undefined && typeof query[key] !== 'string')
    || ['page', 'limit'].some(key => query[key] !== undefined && query[key] !== '' && !integerQuery(query[key]))
    || query.all !== undefined && !['true', 'false', true, false, ''].includes(query.all)) {
    throw new UserRequestContractError('expected scalar directory filters, integer pagination and boolean all.');
  }
  return query;
}
export function readUserDiscoveryQuery(query = {}) {
  if (!record(query) || query.count !== undefined && query.count !== '' && (!integerQuery(query.count) || Number(query.count) < 0)) {
    throw new UserRequestContractError('count must be a nonnegative safe integer.');
  }
  return query;
}
export function readEntityBatchRequest(payload = {}) {
  if (!record(payload) || ['users', 'members', 'guests'].some(key => payload[key] !== undefined
    && (!Array.isArray(payload[key]) || payload[key].some(value => typeof value !== 'string')))) {
    throw new UserRequestContractError('entity groups must be string arrays.');
  }
  // Existing callers can send stale/invalid ID strings: these are omitted by
  // the server. The limit counts valid, distinct IDs separately per group.
  const count = ['users', 'members', 'guests'].reduce((total, key) => total + new Set((payload[key] || [])
    .filter(value => /^[a-f0-9]{24}$/i.test(value)).map(value => value.toLowerCase())).size, 0);
  if (count > 200) throw new UserRequestContractError('too many entity ids (max 200).');
  return payload;
}

export function readMemberDirectoryQuery(query = {}) {
  if (!record(query)
    || Object.keys(query).some(key => !['limit', 'after', 'q', 'typeFilter'].includes(key))
    || query.limit !== undefined && (!integerQuery(query.limit) || Number(query.limit) < 1 || Number(query.limit) > 100)
    || query.after !== undefined && (typeof query.after !== 'string' || !/^[a-f0-9]{24}$/i.test(query.after))
    || query.q !== undefined && (typeof query.q !== 'string' || query.q.length > 100)
    || query.typeFilter !== undefined && !['all', 'user'].includes(query.typeFilter)) {
    throw new UserRequestContractError('expected a bounded member directory query.');
  }
  return query;
}
